
import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithRedirect, 
  getRedirectResult,
  signOut as firebaseSignOut, 
  onAuthStateChanged, 
  updateProfile,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  User as FirebaseUser, 
  Auth,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { FIREBASE_CONFIG } from '../constants';

let auth: Auth | null = null;
let app: FirebaseApp | null = null;

if (FIREBASE_CONFIG.apiKey && FIREBASE_CONFIG.apiKey !== "YOUR_FIREBASE_API_KEY") {
  try {
    app = getApps().length === 0 ? initializeApp(FIREBASE_CONFIG) : getApp();
    auth = getAuth(app);
    setPersistence(auth, browserLocalPersistence).catch(err => console.error("Persistence error:", err.message || err));
  } catch (e: any) {
    console.error("Firebase Initialization Error:", e.message || e);
  }
}

export { app, auth, updateProfile };

export const checkRedirectResult = async () => {
  if (!auth) return null;
  try {
    return await getRedirectResult(auth);
  } catch (e: any) {
    // Suppress known Firebase internal assertion error
    const msg = e.message || e.toString();
    if (msg.includes("Pending promise was never set") || msg.includes("INTERNAL ASSERTION FAILED")) {
        console.debug("Suppressed Firebase Internal Error (safe to ignore):", msg);
        return null;
    }
    // Check for domain blocking on redirect results too
    if (msg.includes('requests-from-referer') || msg.includes('unauthorized-domain')) {
        const err = new Error("Unauthorized Domain");
        (err as any).code = 'auth/unauthorized-domain';
        throw err;
    }
    console.warn("Redirect result check failed:", e.message || e);
    throw e;
  }
};

export const signInWithGoogle = async (sourceApp?: string) => {
  if (!auth) throw new Error("Firebase Auth not initialized.");
  const provider = new GoogleAuthProvider();
  provider.addScope('profile');
  provider.addScope('email');
  provider.setCustomParameters({ prompt: 'consent select_account' });
  
  try {
    const result = await signInWithPopup(auth, provider);
    
    // Extract Google Access Token if needed by the caller
    const credential = GoogleAuthProvider.credentialFromResult(result);
    if (credential) {
        (result as any).googleAccessToken = credential.accessToken;
        (result as any).googleRefreshToken = (credential as any).refreshToken;
    }
    
    if (result.user && sourceApp) {
        // Tag user with sourceApp
        updateProfile(result.user, { displayName: result.user.displayName || undefined }).catch(console.error);
    }
    return result;
  } catch (error: any) {
    // Standardize domain authorization errors
    const isDomainError = 
        error.code === 'auth/unauthorized-domain' || 
        error.code === 'auth/requests-from-referer-blocked' ||
        error.message?.includes('unauthorized-domain') ||
        error.message?.includes('requests-from-referer');

    if (isDomainError) {
        const err = new Error(error.message || "Unauthorized Domain");
        (err as any).code = error.code || 'auth/unauthorized-domain';
        throw err;
    }
    
    if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        try {
            await signInWithRedirect(auth, provider);
        } catch (redirectError: any) {
            if (
                redirectError.code === 'auth/unauthorized-domain' ||
                redirectError.code === 'auth/requests-from-referer-blocked' ||
                redirectError.message?.includes('requests-from-referer')
            ) {
                const err = new Error(redirectError.message || "Unauthorized Domain");
                (err as any).code = redirectError.code || 'auth/unauthorized-domain';
                throw err;
            }
            throw redirectError;
        }
    }
    throw error; // Ensure the original error is re-thrown if not handled
  }
};

export const signInWithEmail = async (email: string, pass: string) => {
    if (!auth) throw new Error("Firebase Auth not initialized.");
    return await signInWithEmailAndPassword(auth, email, pass);
};

export const signUpWithEmail = async (email: string, pass: string, name?: string) => {
    if (!auth) throw new Error("Firebase Auth not initialized.");
    const cred = await createUserWithEmailAndPassword(auth, email, pass);
    if (name) {
        await updateProfile(cred.user, { displayName: name });
    }
    return cred;
};

export const resetPassword = async (email: string) => {
    try {
        const response = await fetch("/api/auth/reset-password", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email })
        });
        
        const data = await response.json().catch(() => ({}));
        if (response.ok && data.success) {
            console.log("Password reset email sent successfully via SMTP!");
            return;
        } else {
            throw new Error(data.error || data.message || "Failed to send password reset email.");
        }
    } catch (err: any) {
        console.error("Password reset error:", err);
        throw new Error(err.message || "Failed to send password reset email.");
    }
};

export const logout = async () => {
  if (!auth) return;
  return firebaseSignOut(auth);
};

export const onAuthStateChange = (callback: (user: FirebaseUser | null) => void) => {
  if (!auth) return () => {};
  return onAuthStateChanged(auth, callback);
};

export const updateUserAvatar = async (photoURL: string) => {
    if (auth && auth.currentUser) {
        await updateProfile(auth.currentUser, { photoURL });
    }
};

export const changePasswordDirectly = async (newPassword: string) => {
    if (!auth || !auth.currentUser) throw new Error("No user is currently signed in.");
    await updatePassword(auth.currentUser, newPassword);
};

export const changePasswordWithReauth = async (currentPassword: string, newPassword: string) => {
    if (!auth || !auth.currentUser) throw new Error("No user is currently signed in.");
    if (!auth.currentUser.email) throw new Error("User email is not available.");
    const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
    await updatePassword(auth.currentUser, newPassword);
};
